import type {PropertyDefinition, ValueType} from '@github-ui/custom-properties-types'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {KebabHorizontalIcon, NoEntryFillIcon, SyncIcon, UndoIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, FormControl, IconButton, Text, TextInput, Token} from '@primer/react'
import type {ReactNode} from 'react'

const mixedValuePlaceholder = '(Mixed)'

export function CustomPropertyEditControl({
  propertyValue,
  definition,
  changed,
  mixed = false,
  block = true,
  error,
  onChange,
  onReset,
}: {
  propertyValue: string
  definition: PropertyDefinition
  changed: boolean
  mixed?: boolean
  block?: boolean
  error?: string
  onChange: (value: string) => void
  onReset(): void
}) {
  const {propertyName, description} = definition
  const undoTitle = `Undo ${propertyName} changes`

  const editResetDropdownEnabled = useFeatureFlag('custom_properties_edit_reset_dropdown')

  return (
    <FormControl>
      <FormControl.Label data-testid="property-name">{getControlLabel(definition)}</FormControl.Label>
      <Box sx={{display: 'flex', width: '100%', gap: 2, alignItems: 'center'}}>
        <CustomPropertyInput
          propertyDefinition={definition}
          propertyValue={propertyValue}
          mixed={mixed}
          block={block}
          onChange={onChange}
        />
        {editResetDropdownEnabled ? (
          <ResetDropdown required={definition.required} {...{propertyName, changed, onReset, onChange}} />
        ) : (
          <Box sx={{visibility: changed ? 'visible' : 'hidden'}}>
            <IconButton icon={() => <UndoIcon />} onClick={onReset} aria-label={undoTitle} variant="invisible" />
          </Box>
        )}
      </Box>
      {error && <FormControl.Validation variant="error">{error}</FormControl.Validation>}
      <FormControl.Caption>{description}</FormControl.Caption>
    </FormControl>
  )
}

interface CustomPropertyInputProps {
  propertyDefinition: PropertyDefinition
  propertyValue: string
  mixed: boolean
  block: boolean
  onChange: (value: string) => void
}
function CustomPropertyInput(props: CustomPropertyInputProps) {
  const {valueType} = props.propertyDefinition

  switch (valueType) {
    case 'single_select':
      return <SingleSelectPropertyInput {...props} />
    case 'boolean':
      return (
        <SingleSelectPropertyInput
          {...{...props, propertyDefinition: {...props.propertyDefinition, allowedValues: ['true', 'false']}}}
        />
      )
    case 'string':
      return <StringPropertyInput {...props} />
  }
}

function SingleSelectPropertyInput({
  propertyDefinition,
  propertyValue,
  mixed,
  block,
  onChange,
}: CustomPropertyInputProps) {
  const {allowedValues, defaultValue, propertyName} = propertyDefinition
  const placeholder = defaultValue ? `${defaultValue} (default value)` : 'Choose an option'
  const dropdownDisplayValue = propertyValue || placeholder

  return (
    <ActionMenu>
      <ActionMenu.Button
        aria-label={mixed ? mixedValuePlaceholder : `Select ${propertyName}`}
        block={block}
        sx={{span: {flex: 'none'}}}
      >
        {mixed ? mixedValuePlaceholder : dropdownDisplayValue}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium" sx={{maxHeight: '300px', overflowY: 'auto'}}>
        <ActionList selectionVariant="single">
          {(allowedValues || []).map(allowedValue => {
            const isSelected = allowedValue === propertyValue
            return (
              <ActionList.Item
                key={allowedValue}
                selected={isSelected}
                onSelect={() => (isSelected ? onChange('') : onChange(allowedValue))}
              >
                {allowedValue}
                {isSelected && (
                  <ActionList.TrailingVisual sx={{ml: 1}}>
                    <XIcon />
                  </ActionList.TrailingVisual>
                )}
              </ActionList.Item>
            )
          })}
          {defaultValue && (
            <>
              <ActionList.Divider />
              <ActionList.Item onSelect={() => onChange('')}>
                <Box sx={{fontWeight: 'normal'}}>Default value ({defaultValue})</Box>
                <ActionList.Description variant="block" sx={{color: 'fg.muted'}}>
                  Inherit the default value set by the organization
                </ActionList.Description>
              </ActionList.Item>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function StringPropertyInput({propertyDefinition, propertyValue, mixed, block, onChange}: CustomPropertyInputProps) {
  const {defaultValue, propertyName} = propertyDefinition
  const placeholder = mixed ? mixedValuePlaceholder : defaultValue || ''
  return (
    <TextInput
      block={block}
      aria-label={propertyName}
      onChange={event => onChange(event.target.value)}
      value={propertyValue}
      placeholder={placeholder}
    />
  )
}

export function CustomPropertyReadOnlyControl({
  propertyValue,
  definition,
  showPermissionMessage = false,
}: {
  propertyValue: string
  definition: PropertyDefinition
  showPermissionMessage?: boolean
}) {
  const {description, valueType} = definition

  const displayValue: Record<ValueType, ReactNode> = {
    string: propertyValue,
    ['single_select']: <Token sx={{color: 'fg.default'}} text={propertyValue} />,
    boolean: <Token sx={{color: 'fg.default'}} text={propertyValue} />,
  }

  return (
    <FormControl>
      <FormControl.Label data-testid="readonly-property-name">{getControlLabel(definition)}</FormControl.Label>
      <Box sx={{py: 1}}>{propertyValue.length ? displayValue[valueType] : '(Empty)'}</Box>
      <FormControl.Caption>
        <Box>{description}</Box>
        {showPermissionMessage && (
          <Box sx={{mt: 1, display: 'flex', gap: 1, fontWeight: 600, alignItems: 'center'}}>
            <NoEntryFillIcon size={12} />
            <Text>You don&apos;t have permission to edit this property</Text>
          </Box>
        )}
      </FormControl.Caption>
    </FormControl>
  )
}

function getControlLabel({propertyName, required}: PropertyDefinition) {
  return `${propertyName}${required ? ' *' : ''}`
}

function ResetDropdown({
  propertyName,
  required,
  changed,
  onReset,
  onChange,
}: {
  propertyName: string
  required: boolean
  changed: boolean
  onReset(): void
  onChange: (value: string) => void
}) {
  const undoTitle = `Undo ${propertyName} changes`
  const resetToDefaultTitle = `Reset ${propertyName} to default value`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={`Open reset options for ${propertyName}`}
        />
      </ActionMenu.Anchor>

      <ActionMenu.Overlay width="small">
        <ActionList>
          <ActionList.Item
            onSelect={onReset}
            aria-label={undoTitle}
            inactiveText={changed ? undefined : 'No change to undo'}
          >
            <ActionList.LeadingVisual>
              <UndoIcon />
            </ActionList.LeadingVisual>
            <Text sx={{fontWeight: 'bold', display: 'block'}}>Undo</Text>
            <Text sx={{fontSize: 'small', color: 'fg.subtle'}}>Reset to the last used value</Text>
          </ActionList.Item>
          {required && (
            <ActionList.Item onSelect={() => onChange('')} aria-label={resetToDefaultTitle}>
              <ActionList.LeadingVisual>
                <SyncIcon />
              </ActionList.LeadingVisual>
              <Text sx={{fontWeight: 'bold', display: 'block'}}>Reset to default</Text>
              <Text sx={{fontSize: 'small', color: 'fg.subtle'}}>
                Use the default value defined by the organization
              </Text>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ CustomPropertyEditControl.displayName ||= 'CustomPropertyEditControl' } catch {}
try{ CustomPropertyInput.displayName ||= 'CustomPropertyInput' } catch {}
try{ SingleSelectPropertyInput.displayName ||= 'SingleSelectPropertyInput' } catch {}
try{ StringPropertyInput.displayName ||= 'StringPropertyInput' } catch {}
try{ CustomPropertyReadOnlyControl.displayName ||= 'CustomPropertyReadOnlyControl' } catch {}
try{ ResetDropdown.displayName ||= 'ResetDropdown' } catch {}